import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Spinner } from './states';

interface Props<T> {
  text: string;
  /** Trả về false để chặn mở danh sách gợi ý (nơi gọi tự xử lý, ví dụ F7 mở hộp thay hàng). */
  onTextChange: (v: string) => void | false;
  search: (q: string) => Promise<T[]>;
  onSelect: (item: T) => void;
  renderItem: (item: T, active: boolean) => ReactNode;
  getKey: (item: T) => string | number;
  placeholder?: string;
  className?: string;
  dropdownWidth?: number;
  footer?: (close: () => void) => ReactNode;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  disabled?: boolean;
  minChars?: number;
  emptyText?: string;
}

export interface ComboboxHandle {
  focus: () => void;
  isOpen: () => boolean;
}

/**
 * Ô tìm kiếm có danh sách gợi ý (bàn phím: ↑↓ chọn, Enter lấy, Esc đóng, Alt+↓ mở).
 * Danh sách render qua portal nên không bị cắt trong bảng có thanh cuộn.
 */
function ComboboxInner<T>(
  { text, onTextChange, search, onSelect, renderItem, getKey, placeholder, className, dropdownWidth = 560, footer, onKeyDown, onFocus, disabled, minChars = 1, emptyText = 'Không tìm thấy' }: Props<T>,
  ref: React.Ref<ComboboxHandle>,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState<string | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const seq = useRef(0);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus(), isOpen: () => open }), [open]);

  useEffect(() => {
    if (query === null) return;
    if (query.trim().length < minChars) {
      setItems([]);
      return;
    }
    const id = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      search(query)
        .then((r) => {
          if (id !== seq.current) return; // bỏ kết quả cũ về muộn
          setItems(r);
          setActive(0);
        })
        .catch(() => id === seq.current && setItems([]))
        .finally(() => id === seq.current && setLoading(false));
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => inputRef.current && setRect(inputRef.current.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery(null);
  };
  const choose = (item: T) => {
    close();
    onSelect(item);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((a) => Math.min(items.length - 1, a + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
        return;
      }
      if (e.key === 'Enter' && items[active]) {
        e.preventDefault();
        choose(items[active]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
    } else if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setQuery(text);
      return;
    }
    onKeyDown?.(e);
  };

  const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
  const width = Math.min(dropdownWidth, window.innerWidth - 16);
  const left = rect ? Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) : 0;
  const style = rect
    ? spaceBelow > 260
      ? { top: rect.bottom + 2, left, width }
      : { bottom: window.innerHeight - rect.top + 2, left, width }
    : undefined;

  return (
    <>
      <input
        ref={inputRef}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onChange={(e) => {
          if (onTextChange(e.target.value) === false) return;
          setOpen(true);
          setQuery(e.target.value);
        }}
        onKeyDown={handleKey}
        onFocus={onFocus}
        onBlur={() => setTimeout(close, 120)}
        role="combobox"
        aria-expanded={open}
      />
      {open &&
        rect &&
        createPortal(
          // Danh sách nằm ngoài hộp thoại (portal): pointer-events-auto để Radix Dialog (đặt body pointer-events:none) không chặn chuột
          <div
            style={style}
            data-combobox-list=""
            className="pointer-events-auto fixed z-[60] max-h-72 overflow-auto rounded-md border bg-card py-1 text-sm shadow-lg"
            onMouseDown={(e) => e.preventDefault()}
          >
            {loading && !items.length && (
              <div className="flex items-center gap-2 px-3 py-2 text-muted-foreground">
                <Spinner /> Đang tìm...
              </div>
            )}
            {!loading && !items.length && (query?.trim().length ?? 0) >= minChars && <div className="px-3 py-2 text-muted-foreground">{emptyText}</div>}
            {items.map((it, i) => (
              <div
                key={getKey(it)}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(it)}
                className={cn('cursor-pointer px-3 py-1.5', i === active && 'bg-accent')}
              >
                {renderItem(it, i === active)}
              </div>
            ))}
            {footer?.(close)}
          </div>,
          document.body,
        )}
    </>
  );
}

export const Combobox = forwardRef(ComboboxInner) as <T>(p: Props<T> & { ref?: React.Ref<ComboboxHandle> }) => ReturnType<typeof ComboboxInner>;
